/**
 * Defaults + helpers for offer.document_structure (Dokument builder).
 * Contract: { version: 1, blocks: [{ id, type, title, enabled, position, content }] }
 */

export const DOCUMENT_BLOCK_TYPES = {
  COVER: "cover",
  LETTER: "letter",
  PROJECT_DESCRIPTION: "project_description",
  SCOPE: "scope",
  PRICE: "price",
  ALTERNATIVES: "alternatives",
  ATTACHMENTS: "attachments",
  SIGNATURE: "signature",
  CUSTOM_TEXT: "custom_text",
};

/**
 * Block types whose content.body is edited under «Rediger tilbud → Prosjektbeskrivelse»,
 * not in the document builder body editor.
 */
export const OFFER_EDIT_SOURCED_BLOCK_TYPES = new Set([
  DOCUMENT_BLOCK_TYPES.PROJECT_DESCRIPTION,
  DOCUMENT_BLOCK_TYPES.SCOPE,
]);

/** Block types whose content.body is free-text editable in the builder. */
export const TEXT_EDITABLE_BLOCK_TYPES = new Set([
  DOCUMENT_BLOCK_TYPES.LETTER,
  DOCUMENT_BLOCK_TYPES.ALTERNATIVES,
  DOCUMENT_BLOCK_TYPES.SIGNATURE,
  DOCUMENT_BLOCK_TYPES.CUSTOM_TEXT,
]);

/** Mirrors API `Offers::DocumentStructure::HEADING_LEVELS`. */
export const HEADING_LEVELS = [1, 2, 3, 4, 5, 6];

/** Mirrors API `Offers::DocumentStructure::VISIBILITY_VALUES`. */
export const VISIBILITY_VALUES = ["all", "pdf_only", "screen_only"];

/**
 * Mirrors API `Offers::DocumentStructure::TYPE_CONTENT_SCHEMAS` — additive,
 * per-type whitelisted `content` keys the inspector renders/round-trips.
 */
export const TYPE_CONTENT_FIELDS = {
  [DOCUMENT_BLOCK_TYPES.COVER]: ["subtitle", "show_customer", "show_offer_date", "image_id"],
  [DOCUMENT_BLOCK_TYPES.LETTER]: ["greeting", "signoff"],
  [DOCUMENT_BLOCK_TYPES.PRICE]: ["show_vat", "show_summary_table", "note"],
  [DOCUMENT_BLOCK_TYPES.SIGNATURE]: ["show_date_field", "show_title_field", "signatory_count"],
};

const SPACING_SIDES = ["top", "right", "bottom", "left"];

const DEFAULT_TYPE_ORDER = [
  DOCUMENT_BLOCK_TYPES.COVER,
  DOCUMENT_BLOCK_TYPES.LETTER,
  DOCUMENT_BLOCK_TYPES.PROJECT_DESCRIPTION,
  DOCUMENT_BLOCK_TYPES.SCOPE,
  DOCUMENT_BLOCK_TYPES.PRICE,
  DOCUMENT_BLOCK_TYPES.ALTERNATIVES,
  DOCUMENT_BLOCK_TYPES.ATTACHMENTS,
  DOCUMENT_BLOCK_TYPES.SIGNATURE,
];

export function newDocumentBlockId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `blk_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function stripHtmlToText(html) {
  if (!html || typeof html !== "string") return "";
  if (!html.includes("<")) return html;
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return (doc.body?.textContent || "").replace(/\s+\n/g, "\n").trim();
  } catch {
    return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }
}

function blockTitleKey(type) {
  return `offers.documentBuilder.blocks.${type}`;
}

/** Accepts a uniform number or a partial `{top,right,bottom,left}` object. */
export function normalizeSpacing(raw) {
  if (raw == null) return null;

  if (typeof raw === "number" && Number.isFinite(raw)) {
    return { top: raw, right: raw, bottom: raw, left: raw };
  }

  if (typeof raw === "object") {
    const out = {};
    let any = false;
    SPACING_SIDES.forEach((side) => {
      const value = Number(raw[side]);
      if (raw[side] !== undefined && raw[side] !== null && Number.isFinite(value)) {
        out[side] = value;
        any = true;
      }
    });
    return any ? out : null;
  }

  return null;
}

export function normalizeHeadingLevel(raw) {
  if (raw == null || raw === "") return null;
  const level = Number(raw);
  return Number.isInteger(level) && HEADING_LEVELS.includes(level) ? level : null;
}

export function normalizeVisibility(raw) {
  return VISIBILITY_VALUES.includes(raw) ? raw : null;
}

/**
 * Normalize a block's `content` hash: keeps `body` (always a string), plus
 * optional padding/margin/visibility and per-type extras (see
 * `TYPE_CONTENT_FIELDS`). Legacy `{ body }`-only payloads keep working.
 */
export function normalizeBlockContent(raw, type) {
  const source = raw && typeof raw === "object" ? raw : {};
  const content = { body: typeof source.body === "string" ? source.body : source.body ?? "" };

  const padding = normalizeSpacing(source.padding);
  if (padding) content.padding = padding;

  const margin = normalizeSpacing(source.margin);
  if (margin) content.margin = margin;

  const visibility = normalizeVisibility(source.visibility);
  if (visibility) content.visibility = visibility;

  (TYPE_CONTENT_FIELDS[type] || []).forEach((key) => {
    if (source[key] !== undefined && source[key] !== null && source[key] !== "") {
      content[key] = source[key];
    }
  });

  return content;
}

/**
 * Prepare a block's `content` for the `persistStructure` payload — drops
 * empty/undefined extras so we don't send noisy keys, but keeps explicit
 * `false` booleans (a deliberately unchecked toggle).
 */
export function serializeBlockContent(content) {
  const source = content && typeof content === "object" ? content : {};
  const payload = { body: typeof source.body === "string" ? source.body : source.body ?? "" };

  if (source.padding) payload.padding = source.padding;
  if (source.margin) payload.margin = source.margin;
  if (source.visibility) payload.visibility = source.visibility;

  Object.keys(source).forEach((key) => {
    if (["body", "padding", "margin", "visibility"].includes(key)) return;
    const value = source[key];
    if (value !== undefined && value !== null && value !== "") {
      payload[key] = value;
    }
  });

  return payload;
}

/**
 * @param {Function} t i18n t()
 * @param {object} [offer]
 */
export function createDefaultDocumentStructure(t, offer = null) {
  const descriptionSeed = stripHtmlToText(offer?.description || offer?.comment || "");

  const blocks = DEFAULT_TYPE_ORDER.map((type, position) => {
    let body = "";
    if (
      type === DOCUMENT_BLOCK_TYPES.LETTER ||
      type === DOCUMENT_BLOCK_TYPES.PROJECT_DESCRIPTION ||
      type === DOCUMENT_BLOCK_TYPES.SCOPE
    ) {
      body = descriptionSeed;
    }

    // Deterministic ids match API Offers::DocumentStructure defaults.
    const content = { body };
    if (type === DOCUMENT_BLOCK_TYPES.SIGNATURE) {
      content.signatory_count = 2;
      content.show_date_field = true;
      content.show_title_field = true;
    }

    return {
      id: `block-${type}`,
      type,
      title: t(blockTitleKey(type)),
      enabled: true,
      show_in_pdf: true,
      page_break_before: false,
      heading_level: null,
      position,
      content,
    };
  });

  return { version: 1, blocks };
}

/**
 * Normalize API payload or fall back to defaults when null/empty.
 * @param {object|null} raw
 * @param {Function} t
 * @param {object} [offer]
 */
export function normalizeDocumentStructure(raw, t, offer = null) {
  if (!raw || typeof raw !== "object" || !Array.isArray(raw.blocks) || raw.blocks.length === 0) {
    return createDefaultDocumentStructure(t, offer);
  }

  const blocks = raw.blocks
    .map((block, index) => {
      if (!block || typeof block !== "object") return null;
      const type = String(block.type || DOCUMENT_BLOCK_TYPES.CUSTOM_TEXT);
      const title =
        (typeof block.title === "string" && block.title.trim()) ||
        t(blockTitleKey(type), { defaultValue: t("offers.documentBuilder.blocks.custom_text") });

      return {
        id: block.id || newDocumentBlockId(),
        type,
        title,
        enabled: block.enabled !== false,
        show_in_pdf: block.show_in_pdf !== false,
        page_break_before: block.page_break_before === true,
        heading_level: normalizeHeadingLevel(block.heading_level),
        position: typeof block.position === "number" ? block.position : index,
        content: normalizeBlockContent(block.content, type),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.position - b.position)
    .map((block, index) => ({ ...block, position: index }));

  if (blocks.length === 0) {
    return createDefaultDocumentStructure(t, offer);
  }

  return {
    version: typeof raw.version === "number" ? raw.version : 1,
    blocks,
  };
}

export function createCustomTextBlock(t, position) {
  return {
    id: newDocumentBlockId(),
    type: DOCUMENT_BLOCK_TYPES.CUSTOM_TEXT,
    title: t("offers.documentBuilder.blocks.custom_text"),
    enabled: true,
    show_in_pdf: true,
    page_break_before: false,
    heading_level: null,
    position,
    content: { body: "" },
  };
}

/** Re-index position after reorder / insert / remove. */
export function reindexDocumentBlocks(blocks) {
  return blocks.map((block, index) => ({ ...block, position: index }));
}

/** Read `content.body` for the first block of `type`, or empty string. */
export function getDocumentBlockBodyByType(structure, type) {
  const block = (structure?.blocks || []).find((b) => b?.type === type);
  const body = block?.content?.body;
  return typeof body === "string" ? body : "";
}

/**
 * Upsert `content.body` for a block type. Ensures a default block exists when
 * missing (e.g. older/custom structures), then reindexes positions.
 *
 * @param {object|null} structure
 * @param {string} type
 * @param {string} body
 * @param {Function} t
 * @param {object} [offer]
 */
export function upsertDocumentBlockBodyByType(structure, type, body, t, offer = null) {
  const normalized = normalizeDocumentStructure(structure, t, offer);
  const nextBody = typeof body === "string" ? body : "";
  let found = false;

  const blocks = normalized.blocks.map((block) => {
    if (block.type !== type) return block;
    found = true;
    return {
      ...block,
      content: {
        ...block.content,
        body: nextBody,
      },
    };
  });

  if (!found) {
    const defaults = createDefaultDocumentStructure(t, offer);
    const seed = defaults.blocks.find((b) => b.type === type);
    if (seed) {
      blocks.push({
        ...seed,
        content: { ...seed.content, body: nextBody },
        position: blocks.length,
      });
    }
  }

  return {
    ...normalized,
    blocks: reindexDocumentBlocks(blocks),
  };
}

/**
 * Apply offer-edit sourced bodies (project_description + scope) onto a structure.
 */
export function upsertOfferEditSourcedBodies(
  structure,
  { projectDescription, scope },
  t,
  offer = null
) {
  let next = normalizeDocumentStructure(structure, t, offer);
  next = upsertDocumentBlockBodyByType(
    next,
    DOCUMENT_BLOCK_TYPES.PROJECT_DESCRIPTION,
    projectDescription,
    t,
    offer
  );
  next = upsertDocumentBlockBodyByType(
    next,
    DOCUMENT_BLOCK_TYPES.SCOPE,
    scope,
    t,
    offer
  );
  return next;
}

/** Payload shape for `updateOfferAsync({ document_structure })`. */
export function serializeDocumentStructure(structure) {
  return {
    version: structure?.version || 1,
    blocks: (structure?.blocks || []).map((block, index) => ({
      id: block.id,
      type: block.type,
      title: block.title,
      enabled: block.enabled !== false,
      show_in_pdf: block.show_in_pdf !== false,
      page_break_before: block.page_break_before === true,
      heading_level: block.heading_level ?? null,
      position: index,
      content: serializeBlockContent(block.content),
    })),
  };
}

/**
 * Resolve a user-facing revision number (never raw DB ids).
 */
export function resolveOfferRevisionLabel(offer) {
  if (!offer) return null;

  const embedded =
    offer.current_draft_revision?.revision_number ??
    offer.accepted_revision?.revision_number;
  if (embedded != null) return embedded;

  if (Array.isArray(offer.revisions) && offer.revisions.length > 0) {
    const numbers = offer.revisions
      .map((rev) => rev?.revision_number)
      .filter((num) => num != null);
    if (numbers.length > 0) return Math.max(...numbers);
  }

  if (offer.revision_number != null) return offer.revision_number;
  if (offer.version != null) return offer.version;
  return null;
}

export function resolveOfferDocumentTitle(offer) {
  if (!offer) return "";
  return (
    offer.cover_page_title ||
    offer.displayTitle ||
    offer.name ||
    offer.offer_number ||
    ""
  );
}

export function resolveOfferCustomerName(offer) {
  if (!offer) return "";
  return offer.customer?.name || offer.customer?.displayTitle || "";
}

export function resolveOfferIdRevSubtitle(offer, t) {
  if (!offer) return "";
  const idPart = offer.offer_number || (offer.id != null ? `#${offer.id}` : "");
  const rev = resolveOfferRevisionLabel(offer);
  const revPart =
    rev != null ? t("offers.documentBuilder.preview.rev", { rev }) : "";
  const idRev = [idPart, revPart].filter(Boolean).join(" · ");
  const customer = resolveOfferCustomerName(offer);
  if (idRev && customer) return `${idRev} — ${customer}`;
  return idRev || customer || "";
}
